import { useEffect, useMemo, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { findTheme } from '@dutchie-pro-partners/theme-registry';

import {
  clearBootstrapThemeFailure,
  getThemeKeyFromModulePath,
  hasBootstrapThemeFailed,
  PARTNER_THEME_SSR_FLAG,
  PREWARM_TIMEOUT_MS,
} from './prewarm-client-utils';
import { abandonInFlightLoad, getLoadedTheme, loadTheme, normalizeThemeScope } from './theme-cache';

export type RegistryThemeStatus = 'failed' | 'idle' | 'loading' | 'ready';

type RegistryTheme = {
  status: RegistryThemeStatus;
  theme: ReturnType<typeof getLoadedTheme>;
};

// Owns the whole partner-theme registry lifecycle for the current theme key. `failed` is latched
// until the key changes, so a late cache write can never swap a mounted federated theme out
// mid-visit, and returning to a previously failed key starts a fresh attempt.
export function useRegistryTheme({
  disableSsr,
  hasLivePreviewManifest,
  manifestUrl,
  modulePath,
  scope,
}: {
  disableSsr?: boolean | null;
  hasLivePreviewManifest: boolean;
  manifestUrl?: string | null;
  modulePath: string | null | undefined;
  scope: string | null | undefined;
}): RegistryTheme {
  const [, setCacheRevision] = useState(0);
  // useFlags() is seeded from the same ldBootstrap that getServerSideProps reads, on the server and
  // on the first client render, so both agree on this flag and hydration cannot diverge on it.
  const flags = useFlags();
  const isEligible =
    flags[PARTNER_THEME_SSR_FLAG] === true && disableSsr !== true && !hasLivePreviewManifest && Boolean(manifestUrl);
  const themeKey = getThemeKeyFromModulePath(modulePath);
  const record = useMemo(
    () =>
      isEligible && scope && themeKey && manifestUrl && typeof findTheme === 'function'
        ? findTheme({ scope: normalizeThemeScope(scope), themeKey, manifestUrl })
        : null,
    [isEligible, scope, themeKey, manifestUrl]
  );
  const loadKey = record?.id ?? null;

  // Seeded once per mount from the browser bootstrap result: if SSR prewarmed a theme but the
  // bootstrap failed, render #1 holds slots blank (known React 18 hydration mismatch) and the
  // client falls straight back to Module Federation instead of racing the registry a second time.
  const [failedKey, setFailedKey] = useState<string | null>(() =>
    loadKey && hasBootstrapThemeFailed(loadKey) ? loadKey : null
  );
  const [trackedKey, setTrackedKey] = useState(loadKey);
  if (trackedKey !== loadKey) {
    setTrackedKey(loadKey);
    setFailedKey(null);
  }

  const hasFailed = loadKey !== null && failedKey === loadKey;
  const loadedTheme = loadKey && !hasFailed ? getLoadedTheme(loadKey) : undefined;

  let status: RegistryThemeStatus = 'idle';
  if (loadKey) {
    if (hasFailed) status = 'failed';
    else if (loadedTheme) status = 'ready';
    else status = 'loading';
  }

  useEffect(() => {
    if (!loadKey || !scope || !themeKey || !manifestUrl || status === 'ready') {
      return undefined;
    }
    // The singleton is one-shot: consume it here (not during render) so React 18 StrictMode
    // re-runs still see the failure latched in `failedKey`.
    if (hasBootstrapThemeFailed(loadKey)) {
      clearBootstrapThemeFailure(loadKey);
      setFailedKey(loadKey);
      return undefined;
    }
    if (status === 'failed') {
      return undefined;
    }

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const fail = (): void => setFailedKey(loadKey);

    const loadPromise = loadTheme(record);
    const timeoutPromise = new Promise<'timeout'>((resolve) => {
      timeoutId = setTimeout(() => resolve('timeout'), PREWARM_TIMEOUT_MS);
    });

    Promise.race([loadPromise.catch(() => null), timeoutPromise])
      .then((result) => {
        if (timeoutId !== undefined) clearTimeout(timeoutId);
        if (cancelled) return;
        if (result === 'timeout') {
          abandonInFlightLoad(loadKey, loadPromise);
          fail();
        } else if (result) {
          setCacheRevision((revision) => revision + 1);
        } else {
          fail();
        }
      })
      .catch(() => {
        if (timeoutId !== undefined) clearTimeout(timeoutId);
        if (!cancelled) fail();
      });

    return () => {
      cancelled = true;
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    };
  }, [loadKey, scope, themeKey, status, manifestUrl, record]);

  return { status, theme: loadedTheme };
}
